import React, { useState, useEffect } from 'react';
import { Pencil, Trash2, Plus, Search } from 'lucide-react';
import api from '../../api';

export default function AdminCategories() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        color: '#6366F1',
        icon: '📁'
    });

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        try {
            setLoading(true);
            const data = await api.getAdminCategories();
            setCategories(data);
        } catch (error) {
            console.error('Error loading categories:', error);
            // Show user-friendly error message
            if (error.message.includes('401') || error.message.includes('Not authenticated')) {
                alert('Please log in as admin to manage categories');
            } else if (error.message.includes('Failed to fetch')) {
                alert('Could not connect to server. Please ensure the backend is running.');
            } else {
                alert('Error loading categories: ' + error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await api.createCategory(formData);
            await loadCategories();
            handleCloseModal();
        } catch (error) {
            alert('Error saving category: ' + error.message);
        }
    };

    const handleDelete = async (category) => {
        if (!window.confirm(`Delete category "${category.name}"? This will fail if events use this category.`)) return;

        try {
            await api.deleteCategory(category.id);
            await loadCategories();
        } catch (error) {
            alert(error.message);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({ name: '', description: '', color: '#6366F1', icon: '📁' });
    };

    const filteredCategories = categories.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
                <div>
                    <h1 className="admin-page-title" style={{ marginBottom: '8px' }}>Categories</h1>
                    <p style={{ color: 'var(--v2-text-muted)' }}>
                        Manage event categories
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <button 
                        onClick={() => setShowModal(true)}
                        style={{ 
                            display: 'flex', alignItems: 'center', gap: '8px',
                            padding: '10px 20px', borderRadius: '12px',
                            background: 'var(--v2-primary)', border: 'none',
                            color: 'white', fontWeight: '600', fontSize: '14px',
                            cursor: 'pointer', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
                            textDecoration: 'none'
                        }}
                    >
                        <Plus size={18} /> Add Category
                    </button>
                </div>
            </div>

            <div className="admin-search-wrapper" style={{ maxWidth: '500px', marginBottom: '32px' }}>
                <Search size={18} className="admin-search-icon" />
                <input
                    type="text"
                    placeholder="Search categories..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="admin-search-input"
                />
            </div>

            {loading ? (
                <div className="admin-card-premium" style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: '40px', height: '40px', border: '3px solid var(--v2-border)', borderTopColor: 'var(--v2-primary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                </div>
            ) : (
                <div className="admin-table-container">
                    <table className="admin-table-premium">
                        <thead>
                            <tr>
                                <th style={{ width: '80px' }}>Icon</th>
                                <th>Category Details</th>
                                <th>Visuals</th>
                                <th>Usage</th>
                                <th style={{ width: '100px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCategories.map((category) => (
                                <tr key={category.id}>
                                    <td>
                                        <div style={{ 
                                            fontSize: '32px', 
                                            width: '56px', 
                                            height: '56px', 
                                            background: '#F9FAFB',
                                            borderRadius: '14px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            {category.icon}
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ fontWeight: '700', fontSize: '15px', color: 'var(--v2-text-main)' }}>{category.name}</div>
                                        {category.description && (
                                            <div style={{ fontSize: '12px', color: 'var(--v2-text-muted)', marginTop: '2px' }}>
                                                {category.description}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{
                                                width: '16px',
                                                height: '16px',
                                                borderRadius: '50%',
                                                background: category.color,
                                                border: '2px solid white',
                                                boxShadow: '0 0 0 1px #ddd'
                                            }} />
                                            <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--v2-text-main)' }}>{category.color}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="status-tag role" style={{ background: 'var(--v2-primary-soft)', color: 'var(--v2-primary)', fontWeight: '700' }}>
                                            {category.event_count || 0} events
                                        </span>
                                    </td>
                                    <td>
                                        <div className="table-actions">
                                            <button
                                                onClick={() => handleDelete(category)}
                                                className="table-action-btn delete"
                                                title="Delete Category"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Category Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && handleCloseModal()}>
                    <div className="modal-content" style={{ maxWidth: '500px' }}>
                        <h3 style={{ marginBottom: '24px', color: 'var(--text-primary)' }}>
                            Add Category
                        </h3>
                        <form onSubmit={handleSubmit}>
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: '500',
                                    color: 'var(--text-primary)',
                                    fontSize: '14px'
                                }}>
                                    Name <span style={{ color: 'var(--error)' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                    maxLength={50}
                                    placeholder="e.g., Poetry, Drama, Sports"
                                />
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: '500',
                                    color: 'var(--text-primary)',
                                    fontSize: '14px'
                                }}>
                                    Description
                                </label>
                                <textarea
                                    className="form-input"
                                    rows="3"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Brief description of this category"
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                                <div>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '8px',
                                        fontWeight: '500',
                                        color: 'var(--text-primary)',
                                        fontSize: '14px'
                                    }}>
                                        Color
                                    </label>
                                    <input
                                        type="color"
                                        className="form-input"
                                        value={formData.color}
                                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                        style={{ height: '45px', cursor: 'pointer' }}
                                    />
                                </div>
                                <div>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '8px',
                                        fontWeight: '500',
                                        color: 'var(--text-primary)',
                                        fontSize: '14px'
                                    }}>
                                        Icon (Emoji)
                                    </label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={formData.icon}
                                        onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                                        maxLength={2}
                                        placeholder="📁"
                                        style={{ fontSize: '24px', textAlign: 'center' }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Create
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
